import { initializeApp } from 'firebase/app';
import { getToken, getMessaging, onMessage, isSupported, Messaging } from 'firebase/messaging';
import { toast } from 'react-toastify';


export const firebaseConfig = {
    apiKey: process.env.REACT_APP_API_KEY,
    authDomain: process.env.REACT_APP_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_PROJECT_ID,
    storageBucket: process.env.REACT_APP_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_APP_ID,
    // measurementId: "G-H1XJB4CCX3"
};

// console.log('*** Environment ***', process.env.REACT_APP_ENV)
// console.log('*** Firebase Config ***', firebaseConfig)

const firebaseApp = initializeApp(firebaseConfig);

// getMessaging() throws in browsers that don't support FCM (e.g. Safari
// without the Push API, insecure origins) — init lazily behind isSupported()
// so importing this module never crashes the app.
let messaging: Messaging | null = null;

const getMessagingInstance = async (): Promise<Messaging | null> => {
    if (messaging) return messaging;
    try {
        if (await isSupported()) {
            messaging = getMessaging(firebaseApp);
        }
    } catch (err) {
        console.error('Firebase messaging is not supported in this browser.', err);
    }
    return messaging;
};

// register() resolves while the worker is still installing, but
// PushManager.subscribe (called inside getToken) requires an active worker —
// wait for activation before handing the registration back.
const waitForServiceWorkerActivation = (registration: ServiceWorkerRegistration): Promise<ServiceWorkerRegistration> =>
    new Promise((resolve, reject) => {
        if (registration.active) {
            resolve(registration);
            return;
        }
        const serviceWorker = registration.installing || registration.waiting;
        if (!serviceWorker) {
            reject(new Error('Service worker registration has no installing/waiting worker.'));
            return;
        }
        serviceWorker.addEventListener('statechange', function onStateChange() {
            if (serviceWorker.state === 'activated') {
                serviceWorker.removeEventListener('statechange', onStateChange);
                resolve(registration);
            } else if (serviceWorker.state === 'redundant') {
                serviceWorker.removeEventListener('statechange', onStateChange);
                reject(new Error('Service worker became redundant before activation.'));
            }
        });
    });

export const getOrRegisterServiceWorker = () => {
    if ('serviceWorker' in navigator) {
        return window.navigator.serviceWorker
            .getRegistration('/firebase-push-notification-scope')
            .then((serviceWorker) => {
                if (serviceWorker) return serviceWorker;
                return window.navigator.serviceWorker.register('/firebase-messaging-sw.js', {
                    scope: '/firebase-push-notification-scope',
                });
            })
            .then(waitForServiceWorkerActivation);
    }
    return Promise.reject(new Error('The browser doesn`t support service worker.'));
};

export const getFirebaseToken = async (): Promise<string | null> => {
    try {
        const messagingInstance = await getMessagingInstance();
        if (!messagingInstance) return null;
        const serviceWorkerRegistration = await getOrRegisterServiceWorker();
        return await getToken(messagingInstance, { vapidKey: process.env.REACT_APP_VAPID_KEY, serviceWorkerRegistration });
    } catch (err) {
        console.error('An error occured while retrieving firebase token. ', err);
        return null;
    }
};

export const onForegroundMessage = async () => {
    const messagingInstance = await getMessagingInstance();
    if (!messagingInstance) {
        throw new Error('Firebase messaging is not supported in this browser.');
    }
    return new Promise((resolve) => onMessage(messagingInstance, (payload: any) => {
        //console.log('*** Foreground Message ***', payload)
        toast.success(`${payload.notification.body}`, {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "light",

        });
        resolve(payload)

    }));
};

